// pages/doctors.tsx
import React, { useState } from "react"
import { IntlProvider, useTranslations } from "next-intl"
import { GetStaticProps } from "next"
import classNames from "classnames"
import Image from "next/image"
import Head from "next/head"
import SkinuvitaSeoHead from "../src/components/LandingPage/SkinuvitaSeoHead"
import { Routes } from "../src/data/routes"
import { push } from "@socialgouv/matomo-next"
import styles from "../styles/LandingPage/Patients.module.scss"
import SText from "../src/components/Text/SText"
import NavBarPatients from "../src/components/LandingPage/navbars/NavBarPatients"
import SectionTwo from "../src/components/Patient/Sections/SectionTwo"
import SectionThree from "../src/components/Patient/Sections/SectionThree"
import SectionFour from "../src/components/Patient/Sections/SectionFour"
import SectionFive from "../src/components/Patient/Sections/SectionFive"
import SectionSix from "../src/components/Patient/Sections/SectionSix"
import SectionEight from "../src/components/Patient/Sections/SectionEight"
import SectionNine from "../src/components/Patient/Sections/SectionNine"
import SectionThirteen from "../src/components/Patient/Sections/SectionThirteen"
import ForDoctorsModal from "../src/components/modals/ForDoctorsModal"
import PartnerModal from "../src/components/modals/PartnerModal"
import Explanation, { DoctorExplanation } from "../src/content/Explanation"
import AboutUs from "../src/content/AboutUs"
import Team from "../src/content/Team"
import Advisor from "../src/content/Advisor"
import Awards from "../src/content/Awards"
import Network from "../src/content/Network"
import DoctorHero from "../src/heroes/DoctorHero"
import NewsletterModal from "../src/components/modals/NewsletterModal"
import SkinuvitaDrawer from "../src/components/drawer/skinuvita_drawer"
import ContactArea from "src/components/ContactArea/ContactArea"
import Footer from "src/components/footer"
import skColors from "../styles/skColors"
import { useRouter } from "next/router"
import LanguageToggle from "src/components/LanguageToggle"

export enum ModalState {
  NEWSLETTER = "NEWSLETTER",
  FOR_DOCTORS = "FOR_DOCTORS",
  BECOME_PARTNER = "BECOME_PARTNER",
  CONTACT = "CONTACT",
}

/**
 * DoctorsContent - uses index namespace and the doctorsection keys inside it.
 * (Your translations live under index.doctorsection.* in src/intl/*.json)
 */
function DoctorsContent() {
  const t = useTranslations("index")
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState<ModalState | null>(null)

  const closeModal = () => setIsModalOpen(null)

  return (
    <div className={styles.root}>
      <SkinuvitaSeoHead
        title={t("doctors.pageTitle")}
        description={t("doctors.pageDescription")}
        canonicalPath={Routes.doctors}
      />

      <NavBarPatients />
      <div className={styles.constrainWidth}>
        <div className={styles.behindNavBar}></div>

        <DoctorHero
          onPrimaryButtonClick={() => {
            push(["trackEvent", "doctor", "newsletter", "clicked"])
            setIsModalOpen(ModalState.NEWSLETTER)
          }}
        />

        <SectionTwo backgroundColor={"white"}>
          <div className={classNames(styles.row, styles.spaceAround, styles.sectionTwoTitle)}>
            <div className={classNames(styles.wideBox, styles.textCenter)}>
              <SText variant={"title"} color={"turq500"} className={styles.adaptiveTitleSize}>
                {t("doctors.benefitsHeading")}
              </SText>
            </div>
          </div>

          <div className={classNames(styles.row, styles.spaceAround, styles.flwr)}>
            <div className={styles.smallImageCol}>
              <div className={styles.smallImageContainer}>
                <Image
                  src={"/images/1_bewaehrte_therapie.webp"}
                  width={92}
                  height={92}
                  alt={"doctors.benefit1.alt"}
                  style={{ maxWidth: "100%", height: "auto" }}
                />
              </div>
              <SText variant={"body"} color={"turq500"} size={14} style={{ marginBottom: "16px" }}>
                {t("doctors.benefit1.title")}
              </SText>
              <SText
                variant={"body"}
                color={"green500"}
                size={16}
                style={{ textAlign: "center", marginBottom: "16px" }}
              >
                {t("doctors.benefit1.desc")}
              </SText>
            </div>

            <div className={styles.smallImageCol}>
              <div className={styles.smallImageContainer}>
                <Image
                  src={"/images/2_flexibilitaet.webp"}
                  width={92}
                  height={92}
                  alt={"doctorsection.benefit2.alt"}
                  style={{ maxWidth: "100%", height: "auto" }}
                />
              </div>
              <SText variant={"body"} color={"turq500"} size={14} style={{ marginBottom: "16px" }}>
                {t("doctors.benefit2.title")}
              </SText>
              <SText
                variant={"body"}
                color={"green500"}
                size={16}
                style={{ textAlign: "center", marginBottom: "16px" }}
              >
                {t("doctors.benefit2.desc")}
              </SText>
            </div>

            <div className={styles.smallImageCol}>
              <div className={styles.smallImageContainer}>
                <Image
                  src={"/images/3_nachhaltigkeit.webp"}
                  width={92}
                  height={92}
                  alt={"doctors.benefit3.alt"}
                  style={{ maxWidth: "100%", height: "auto" }}
                />
              </div>
              <SText variant={"body"} color={"turq500"} size={14} style={{ marginBottom: "16px" }}>
                {t("doctors.benefit3.title")}
              </SText>
              <SText
                variant={"body"}
                color={"green500"}
                size={16}
                style={{ textAlign: "center", marginBottom: "16px" }}
              >
                {t("doctors.benefit3.desc")}
              </SText>
            </div>

            <div className={styles.smallImageCol}>
              <div className={styles.smallImageContainer}>
                <Image
                  src={"/images/4_faire_verguetung.webp"}
                  width={92}
                  height={92}
                  alt={"doctors.benefit4.alt"}
                  style={{ maxWidth: "100%", height: "auto" }}
                />
              </div>
              <SText variant={"body"} color={"turq500"} size={14} style={{ marginBottom: "16px" }}>
                {t("doctors.benefit4.title")}
              </SText>
              <SText
                variant={"body"}
                color={"green500"}
                size={16}
                style={{ textAlign: "center", marginBottom: "16px" }}
              >
                {t("doctors.benefit4.desc")}
              </SText>
            </div>
          </div>
        </SectionTwo>

        <SectionThree className={styles.explanationSection} backgroundColor={"rgba(248, 248, 248, 1)"}>
          <DoctorExplanation />
        </SectionThree>

        <SectionFour id={"about"}>
          <AboutUs />
        </SectionFour>

        {/* <SectionFive backgroundColor={"rgba(248, 248, 248, 1)"}>
          <Team />
        </SectionFive> */}

        {/* <SectionSix>
          <Advisor />
        </SectionSix> */}

        <SectionEight backgroundColor={"rgba(248, 248, 248, 1)"}>
          <Awards />
        </SectionEight>

        {/* <SectionNine
          backgroundColor={"white"}
          waveColorTop={"rgba(248, 248, 248, 1)"}
          waveColorBottom={"rgba(232, 247, 247, 1)"}
          className={styles.networkSection}
        >
          <Network setIsModalOpen={setIsModalOpen} />
        </SectionNine> */}

        <SectionThirteen>
          <ContactArea />
        </SectionThirteen>

        <Footer />

        {isModalOpen === ModalState.NEWSLETTER && (
          <NewsletterModal closeModal={() => closeModal()} isOpen={isModalOpen === ModalState.NEWSLETTER} />
        )}
        {isModalOpen === ModalState.FOR_DOCTORS && (
          <ForDoctorsModal closeModal={() => closeModal()} isOpen={isModalOpen === ModalState.FOR_DOCTORS} />
        )}
        {isModalOpen === ModalState.BECOME_PARTNER && (
          <PartnerModal closeModal={() => closeModal()} isOpen={isModalOpen === ModalState.BECOME_PARTNER} />
        )}
        <SkinuvitaDrawer drawerOpen={drawerOpen} setDrawerOpen={setDrawerOpen} />
      </div>
    </div>
  )
}

/**
 * Page wrapper: load messages at build time and pass them via props
 */
export default function DoctorsPageWrapper(props: { messages: Record<string, any>; locale: string }) {
  const { messages, locale } = props
  return (
    <IntlProvider locale={locale} messages={messages}>
      <DoctorsContent />
    </IntlProvider>
  )
}

export const getStaticProps: GetStaticProps = async (context) => {
  const locale = (context.locale as string) || "de"

  let messages
  try {
    messages = (await import(`../src/intl/${locale}.json`)).default
  } catch (err) {
    messages = (await import(`../src/intl/de.json`)).default
  }

  return {
    props: {
      messages,
      locale,
    },
  }
}
